import { useMemo } from 'react';
import Link from 'next/link';
import { getLink } from '@frameio/shared/routing/getLink';
import { NavItem, ProjectAvatar, Spinner, theme } from '@frameio/vapor';
import { IconCheckmark, IconChevronRight } from '@frameio/vapor-icons';
import type {
  Project,
  ProjectItemVariant,
  ProjectSelectCallback,
} from '../types';
import TEXT from './generated/TEXT/ProjectItem';

TEXT.DEFINE({
  /** Aria label for the spinner */
  spinnerAriaLabel: 'Spinner',
});

type ProjectItemProps = {
  onSelect: ProjectSelectCallback;
  project: Project;
  variant?: ProjectItemVariant;
  isSelected?: boolean;
  loadingId?: string;
  navOnClick?: boolean;
  query?: string;
};

export function ProjectItem({
  onSelect,
  project,
  variant = 'default',
  isSelected = false,
  loadingId,
  navOnClick = true,
  query,
}: ProjectItemProps) {
  const projectImageUrl = project.images?.image32;
  const isLoading = loadingId === project.id;

  let isVisuallySelected = isSelected;

  // if another project is loading, hide the selected state
  // of the currently activated project
  if (Boolean(loadingId) && isSelected) {
    isVisuallySelected = false;
  }

  const handleSelect = () => {
    onSelect(project);
  };

  const iconAfter = useMemo(() => {
    if (variant === 'noChevrons') return null;
    if (variant === 'projectSwitcher') {
      if (isLoading) {
        return (
          <Spinner
            aria-label={TEXT.spinnerAriaLabel}
            delay={0}
            isLoading={true}
            size={'x-small'}
          />
        );
      }

      if (isVisuallySelected) {
        return <IconCheckmark color={theme.colors.brand.primary} size={16} />;
      }

      return null;
    }
    return <IconChevronRight size={16} />;
  }, [variant, isLoading, isVisuallySelected]);

  const navItemProps = navOnClick
    ? {
        onClick: (e: React.MouseEvent) => e.preventDefault(),
        as: Link,
        href: getLink({ page: 'folder', projectId: project.id }),
      }
    : { as: 'button' as const };

  return (
    <NavItem
      {...navItemProps}
      data-testid={`project-nav--${project.name}`}
      highlight={query}
      iconAfter={iconAfter}
      iconBefore={
        <ProjectAvatar
          id={project.id}
          isInactive={Boolean(project.archivedAt)}
          isPrivate={project.private}
          size={AVATAR_SIZE}
          src={projectImageUrl ?? ''}
          title={project.name}
        />
      }
      isSelected={
        variant === 'projectSwitcher' ? isVisuallySelected || isLoading : false
      }
      label={project.name}
      onClick={handleSelect}
      onKeyDown={(event: React.KeyboardEvent) => {
        if (event.key === 'Enter') {
          handleSelect();
        }
      }}
      tabIndex={0}
    />
  );
}

const AVATAR_SIZE = 20;
